// Data Context - ZERO localStorage usage!
// All data from API + server-side storage only

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Appointment, Patient, Doctor, Service, DashboardStats } from '../types';
import { clinicAPI } from '../services/api';
import toast from 'react-hot-toast';

interface DataContextType {
  // Data
  appointments: Appointment[];
  patients: Patient[];
  doctors: Doctor[];
  services: Service[];
  stats: DashboardStats;
  isLoading: boolean;
  
  // Actions (all API-based, NO localStorage!)
  addAppointment: (appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Appointment>;
  updateAppointment: (id: string, updates: Partial<Appointment>) => Promise<void>;
  deleteAppointment: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Calculate dashboard stats from current data
  const calculateStats = (): DashboardStats => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    const todaysAppointments = appointments.filter(apt =>
      apt.date === today
    );
    
    const completedAppointments = appointments.filter(apt => apt.status === 'completed');
    const cancelledAppointments = appointments.filter(apt => apt.status === 'cancelled');
    const upcomingAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate > now && apt.status !== 'cancelled';
    });

    return {
      totalPatients: patients.length,
      todayAppointments: todaysAppointments.length,
      monthlyRevenue: completedAppointments.length * 150, // Average $150 per appointment
      upcomingAppointments: upcomingAppointments.length,
      completedAppointments: completedAppointments.length,
      cancelledAppointments: cancelledAppointments.length
    };
  };

  const stats = calculateStats();

  // Load all data from API (NO localStorage!)
  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    setIsLoading(true);
    try {
      // Load data from API only
              const [appointmentsRes, patientsRes, doctorsRes, servicesRes] = await Promise.all([
        clinicAPI.getAppointments(),
        clinicAPI.getPatients(),
        clinicAPI.getDoctors(),
        clinicAPI.getServices(),
      ]);
      
        setAppointments(appointmentsRes.success ? appointmentsRes.data || [] : []);
        setPatients(patientsRes.success ? patientsRes.data || [] : []);
        setDoctors(doctorsRes.success ? doctorsRes.data || [] : []);
        setServices(servicesRes.success ? servicesRes.data || [] : []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load clinic data');
    } finally {
      setIsLoading(false);
    }
  };

  // Appointment actions (all API-based)
  const addAppointment = async (appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Appointment> => {
    try {
      const response = await clinicAPI.createAppointment(appointmentData);
      if (response.success) {
        setAppointments(prev => [...prev, response.data]);
        toast.success('Appointment created successfully');
        return response.data;
      }
      throw new Error('Failed to create appointment');
    } catch (error) {
      console.error('Failed to create appointment:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create appointment');
      throw error;
    }
  };

  const updateAppointment = async (id: string, updates: Partial<Appointment>): Promise<void> => {
    try {
      const response = await clinicAPI.updateAppointment(id, updates);
      if (response.success) {
        setAppointments(prev =>
          prev.map(apt =>
            apt.id === id ? { ...apt, ...updates, updatedAt: new Date().toISOString() } : apt
          )
        );
        toast.success('Appointment updated successfully');
      }
    } catch (error) {
      console.error('Failed to update appointment:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update appointment');
      throw error;
    }
  };

  const deleteAppointment = async (id: string): Promise<void> => {
    try {
      const response = await clinicAPI.deleteAppointment(id);
      if (response.success) {
        setAppointments(prev => prev.filter(apt => apt.id !== id));
        toast.success('Appointment deleted successfully');
      }
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete appointment');
      throw error;
    }
  };

  const refreshData = async (): Promise<void> => {
    await initializeData();
    toast.success('Data refreshed successfully');
  };

  const value = {
    appointments,
    patients,
    doctors,
    services,
    stats,
    isLoading,
    addAppointment,
    updateAppointment,
    deleteAppointment,
    refreshData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};